import { ArrowLeft } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useAuth } from '@/auth/use-auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AuthCard } from '@/pages/auth/auth-card';

/*
 * The 2FA challenge shown after a password login when the account has 2FA on.
 * Accepts a 6-digit TOTP code or (toggled) a one-time recovery code.
 */
interface TwoFactorChallengeProps {
  onVerified: () => void;
  onCancel: () => void;
}

export function TwoFactorChallenge({ onVerified, onCancel }: TwoFactorChallengeProps) {
  const { t } = useTranslation('auth');
  const { verifyTwoFactor } = useAuth();
  const [useRecovery, setUseRecovery] = useState(false);
  const [value, setValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus the code field on mount - this is a dedicated single-task challenge
  // screen, so directing focus here aids rather than disorients (done via ref
  // instead of the flagged autoFocus attribute).
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (value.trim().length === 0) return;
    setSubmitting(true);
    try {
      await verifyTwoFactor(useRecovery ? { recoveryCode: value.trim() } : { code: value.trim() });
      onVerified();
    } catch {
      toast.error(t('errors.invalidCode'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthCard
      title={t('twoFactor.title')}
      footer={
        <button
          type="button"
          className="text-dim hover:text-strong inline-flex items-center gap-1"
          onClick={onCancel}
        >
          <ArrowLeft className="size-3.5" aria-hidden />
          {t('forgotPassword.backToLogin')}
        </button>
      }
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        <p className="font-mono text-xs text-dim">{t('twoFactor.prompt')}</p>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="twofactor-code">
            {useRecovery ? t('fields.recoveryCode') : t('fields.code')}
          </Label>
          <Input
            id="twofactor-code"
            ref={inputRef}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            autoComplete="one-time-code"
            inputMode={useRecovery ? 'text' : 'numeric'}
          />
        </div>
        <Button type="submit" variant="primary" disabled={submitting}>
          {t('twoFactor.submit')}
        </Button>
        <button
          type="button"
          className="font-mono text-xs text-green hover:brightness-110"
          onClick={() => {
            setUseRecovery((prev) => !prev);
            setValue('');
          }}
        >
          {useRecovery ? t('twoFactor.useCode') : t('twoFactor.useRecovery')}
        </button>
      </form>
    </AuthCard>
  );
}
